'use client';

import { buttonVariants } from '@pickleballinc/ui/components/ui/button';
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationNext,
	PaginationPrevious
} from '@pickleballinc/ui/components/ui/pagination';
import { cn } from '@pickleballinc/ui/lib/utils';
import clsx from 'clsx';

interface PaginationWrapperProps {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	className?: string;
}

const getPageNumbers = (totalPages: number, currentPage: number) => {
	const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];

	if (totalPages <= 6) {
		// Show all pages if 6 or fewer
		for (let i = 1; i <= totalPages; i++) {
			pages.push(i);
		}
	} else {
		// Always show first page
		pages.push(1);

		if (currentPage <= 3) {
			// Near the start
			pages.push(2, 3, 4);
			pages.push('ellipsis-end');
			pages.push(totalPages);
		} else if (currentPage >= totalPages - 2) {
			// Near the end
			pages.push('ellipsis-start');
			pages.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
		} else {
			// In the middle
			pages.push('ellipsis-start');
			pages.push(currentPage - 1, currentPage, currentPage + 1);
			pages.push('ellipsis-end');
			pages.push(totalPages);
		}
	}

	return pages;
};

export const PaginationWrapper = ({ currentPage, totalPages, onPageChange, className }: PaginationWrapperProps) => {
	const pageNumbers = getPageNumbers(totalPages, currentPage);
	const isFirstPage = currentPage === 1;
	const isLastPage = currentPage === totalPages;

	const handlePrevious = () => {
		if (!isFirstPage) {
			onPageChange(currentPage - 1);
		}
	};

	const handleNext = () => {
		if (!isLastPage) {
			onPageChange(currentPage + 1);
		}
	};

	return (
		<Pagination>
			<PaginationContent className={cn(className)}>
				<PaginationPrevious
					onClick={handlePrevious}
					className={cn('cursor-pointer', isFirstPage && 'pointer-events-none opacity-50')}
					aria-disabled={isFirstPage}
				/>
				<div
					className={clsx('flex', {
						'hidden sm:flex': totalPages > 3
					})}
				>
					{pageNumbers.map((page) => {
						if (page === 'ellipsis-start' || page === 'ellipsis-end') {
							return (
								<PaginationItem key={page}>
									<PaginationEllipsis />
								</PaginationItem>
							);
						}

						const isActive = page === currentPage;

						return (
							<PaginationItem
								key={page}
								onClick={() => onPageChange(page)}
								className={cn(
									buttonVariants({
										variant: isActive ? 'outline' : 'ghost'
									}),
									'cursor-pointer rounded-full'
								)}
							>
								{page}
							</PaginationItem>
						);
					})}
				</div>
				<PaginationNext
					onClick={handleNext}
					className={cn('cursor-pointer', isLastPage && 'pointer-events-none opacity-50')}
					aria-disabled={isLastPage}
				/>
			</PaginationContent>
		</Pagination>
	);
};
